/**
 * VerComoPacienteAviso — pílula no TOPO da aba aberta por "Portal do paciente".
 *
 * A aba nasce isolada (lib/supabaseClient.ts) com a sessão DO PACIENTE: tudo
 * que aparece é o que ele vê, e tudo que se faz aqui fica registrado como ele.
 * A pílula existe pra ninguém confundir essa aba com a própria conta: diz de
 * quem é o portal e oferece "Fechar".
 *
 * Posição no topo, centralizada, uma linha — mesma regra da NovaVersaoAviso
 * (Valdair 16/set: nada fixo embaixo ou no canto, cobria a Essentia).
 *
 * "Fechar" encerra SÓ a sessão desta aba (scope local). O signOut padrão do
 * GoTrue é global — revogaria a sessão do paciente de verdade no celular dele.
 */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Eye, X, Undo2 } from 'lucide-react';
import { theme } from '../../styles/GlobalStyle';
import { useAuth } from '../../context/AuthContext';
import { isSessaoIsolada, isEmbutido, soltarSessaoIsolada } from '../../lib/supabaseClient';

const TEXTOS = {
  pt: { modo: 'Você está vendo o portal de', fechar: 'Fechar', fechando: 'Fechando…', voltar: 'Voltar a ser eu' },
  en: { modo: 'You are viewing the portal of', fechar: 'Close', fechando: 'Closing…', voltar: 'Back to my account' },
  es: { modo: 'Estás viendo el portal de', fechar: 'Cerrar', fechando: 'Cerrando…', voltar: 'Volver a mi cuenta' },
};

const Pilula = styled.div`
  position: fixed;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: min(640px, calc(100vw - 24px));
  padding: 7px 8px 7px 14px;
  border-radius: 999px;
  background: ${theme.colors.primary};
  color: #fff;
  box-shadow: 0 8px 24px rgba(146, 86, 62, 0.28);
  font-family: 'Satoshi', -apple-system, sans-serif;
  font-size: 13px;
  font-weight: 500;

  > svg { flex: none; opacity: 0.9; }
  .txt { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .txt strong { font-weight: 700; }
`;

const Botao = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  border-radius: 999px;
  padding: 5px 10px 5px 12px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  &:hover:not(:disabled) { background: rgba(255, 255, 255, 0.26); }
  &:disabled { opacity: 0.7; cursor: default; }
`;

export default function VerComoPacienteAviso() {
  const { i18n } = useTranslation();
  const { user, profile } = useAuth();
  const [fechando, setFechando] = useState(false);

  // No pop-up da ficha (iframe) o cabeçalho do pop-up já diz de quem é e fecha.
  const aparece = !!user && isSessaoIsolada() && !isEmbutido();

  // A pílula é fixa no topo e, num celular de 390 px, ficava por cima do título
  // da tela. Em vez de mudá-la de lugar (o rodapé é das abas do app), ela avisa
  // que está aí, e quem tem cabeçalho no topo desce o seu.
  useEffect(() => {
    if (!aparece) return;
    document.documentElement.setAttribute('data-ver-como', '1');
    return () => document.documentElement.removeAttribute('data-ver-como');
  }, [aparece]);

  if (!aparece) return null;

  const lang = (i18n.language || 'en').slice(0, 2) as keyof typeof TEXTOS;
  const t = TEXTOS[lang] || TEXTOS.en;
  const nome = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || user.email || '';

  /** Encerra SÓ a sessão desta aba e apaga a marca de isolamento. Sem isso a aba
   *  segue mandando o token do paciente em tudo (incidente 24/set). */
  const soltarAAba = async () => {
    // sem signOut: o evento correria no canal da storageKey e derrubaria outra
    // janela que esteja vendo o portal (auditoria 24/set).
    soltarSessaoIsolada();
  };

  /** "Voltar a ser eu": mesma aba, conta de quem está trabalhando. A sessão dele
   *  nunca saiu do localStorage — depois do reload o client comum a reencontra. */
  const voltarASerEu = async () => {
    setFechando(true);
    await soltarAAba();
    window.location.replace('/');
  };

  const fechar = async () => {
    setFechando(true);
    await soltarAAba();
    // Aba aberta por script fecha sozinha; se o navegador recusar, a sessão
    // já morreu e a tela cai no login — nada do paciente fica visível.
    window.close();
    window.location.replace('/login');
  };

  return (
    <Pilula role="status" aria-live="polite">
      <Eye size={14} />
      <span className="txt" title={nome}>{t.modo} <strong>{nome}</strong></span>
      <Botao type="button" onClick={voltarASerEu} disabled={fechando}>
        {t.voltar}
        <Undo2 size={13} />
      </Botao>
      <Botao type="button" onClick={fechar} disabled={fechando}>
        {fechando ? t.fechando : t.fechar}
        <X size={13} />
      </Botao>
    </Pilula>
  );
}
